import { useCallback, useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';

import { TtqMultiSolutionAssessModal } from '../../features/ttqib/TtqMultiSolutionAssessModal';
import { TtqWorkbenchSourceParticle } from '../../features/ttqib/TtqWorkbenchSourceParticle';
import { TtqWorkbenchDerivedList, TtqWorkbenchFocusLayout } from '../../features/ttqib/TtqWorkbenchSplitLayout';
import { TtqWorkbenchToolbar, TtqWorkbenchToolbarAction } from '../../features/ttqib/TtqWorkbenchToolbar';
import { useTtqWorkbenchFocusEmphasis } from '../../features/ttqib/useTtqWorkbenchFocusEmphasis';
import { TtqWorkbenchShell, useTtqWorkbenchContext } from '../../features/ttqib/ttqWorkbenchShared';
import {
  ttqWorkbenchService,
  type TtqWorkbenchDerivedItem,
} from '../../features/ttqib/ttqWorkbenchService';
import { fetchWorkbenchParticle } from '../../features/ttqib/ttqWorkbenchParticleCache';
import { candidateNeedsDiagram } from '../../utils/ttqDiagramInference';
import type { AssessTtqMultiSolutionWorkbenchResponse } from '@kelilearning/psycheflow-contracts';
import type { WorkspaceDerivedItem } from '../../services/kpWorkspaceService';

function toWorkspaceDerivedItems(items: TtqWorkbenchDerivedItem[]): WorkspaceDerivedItem[] {
  return items.map((item) => ({
    id: item.id,
    title: item.title || '衍生解法',
    summary: '',
    createdAt: item.createdAt || '',
    source: 'session' as const,
    question: item.question,
    answer: item.answer,
    particleId: item.particleId,
  }));
}

function pickDefaultCandidateIndexes(assessment: AssessTtqMultiSolutionWorkbenchResponse): number[] {
  return assessment.candidates
    .filter((item) => !item.alreadyGenerated && !item.isSourceParticleMethod)
    .map((item) => item.index);
}

export function TtqMultiSolutionWorkbenchPage() {
  const { ttqaId = '', particleId = '' } = useParams();
  const location = useLocation();
  const returnTo = `${location.pathname}${location.search}`;
  const ctx = useTtqWorkbenchContext(ttqaId, particleId);
  const [generating, setGenerating] = useState(false);
  const [assessing, setAssessing] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [assessment, setAssessment] = useState<AssessTtqMultiSolutionWorkbenchResponse | null>(null);
  const [assessError, setAssessError] = useState<string | null>(null);
  const [selectedIndexes, setSelectedIndexes] = useState<number[]>([]);
  const [imageModel, setImageModel] = useState('wan2.2-t2i-flash');
  const [imageModels, setImageModels] = useState<Array<{ id: string; label: string }>>([
    { id: 'wan2.2-t2i-flash', label: '万相 2.2 Flash（推荐）' },
    { id: 'wanx2.1-t2i-turbo', label: '万相 2.1 Turbo' },
  ]);
  const [actionError, setActionError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [derived, setDerived] = useState<TtqWorkbenchDerivedItem[]>([]);
  const split = derived.length > 0;
  const { emphasis, setEmphasis } = useTtqWorkbenchFocusEmphasis(split);

  const loadDerived = useCallback(async () => {
    try {
      const context = await ttqWorkbenchService.getMultiSolutionContext(ttqaId, particleId);
      const previews = ttqWorkbenchService.mapDerivedPreviews(context);
      const enriched = await Promise.all(
        previews.map(async (item) => {
          const particle = await fetchWorkbenchParticle(item.particleId).catch(() => null);
          const question = typeof particle?.questions === 'string'
            ? particle.questions
            : particle?.questions && typeof particle.questions === 'object' && 'main' in particle.questions
              ? String((particle.questions as { main?: string }).main || '')
              : item.question;
          const answer = typeof particle?.answers === 'string'
            ? particle.answers
            : particle?.answers && typeof particle.answers === 'object' && 'main' in particle.answers
              ? String((particle.answers as { main?: string }).main || '')
              : item.answer;
          return {
            ...item,
            title: item.title || particle?.title || '衍生解法',
            question,
            answer,
          };
        }),
      );
      setDerived(enriched);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : '加载解法列表失败');
    }
  }, [ttqaId, particleId]);

  useEffect(() => { void loadDerived(); }, [loadDerived]);

  useEffect(() => {
    if (!ttqaId) return;
    void ttqWorkbenchService.getImageGenerationProviders(ttqaId)
      .then((resp) => {
        const models = resp.providers[0]?.models || [];
        if (models.length) setImageModels(models);
        if (resp.defaultModel) setImageModel(resp.defaultModel);
      })
      .catch(() => undefined);
  }, [ttqaId]);

  const runAssess = useCallback(async () => {
    setAssessing(true);
    setAssessError(null);
    setAssessment(null);
    setSelectedIndexes([]);
    try {
      const result = await ttqWorkbenchService.assessMultiSolution(ttqaId, {
        sourceParticleIds: [particleId],
      });
      setAssessment(result);
      setSelectedIndexes(pickDefaultCandidateIndexes(result));
    } catch (e) {
      setAssessError(e instanceof Error ? e.message : '评估失败');
    } finally {
      setAssessing(false);
    }
  }, [ttqaId, particleId]);

  const handleOpenAssess = () => {
    setActionError(null);
    setSuccessMessage(null);
    setModalOpen(true);
    void runAssess();
  };

  const handleToggleIndex = (index: number) => {
    setSelectedIndexes((prev) => (
      prev.includes(index) ? prev.filter((value) => value !== index) : [...prev, index]
    ));
  };

  const handleConfirmGenerate = async () => {
    if (!assessment || selectedIndexes.length === 0) return;

    const selected = assessment.candidates.filter(
      (item) => selectedIndexes.includes(item.index)
        && !item.alreadyGenerated
        && !item.isSourceParticleMethod,
    );
    if (selected.length === 0) return;

    setGenerating(true);
    setActionError(null);
    setAssessError(null);
    const generatedLabels: string[] = [];
    const errors: string[] = [];

    try {
      for (const candidate of selected) {
        try {
          const needsDiagram = candidateNeedsDiagram(candidate);
          await ttqWorkbenchService.runMultiSolution(ttqaId, {
            sourceParticleIds: [particleId],
            batchSize: 1,
            selectedSolutionIndex: candidate.index,
            approachHint: `${candidate.label}：${candidate.approachSummary}`,
            requiresDiagram: needsDiagram,
            imageGeneration: needsDiagram
              ? { provider: 'dashscope', model: imageModel, enabled: true }
              : undefined,
          });
          generatedLabels.push(candidate.label);
        } catch (e) {
          errors.push(`${candidate.label}：${e instanceof Error ? e.message : '生成失败'}`);
        }
      }

      await loadDerived();

      if (generatedLabels.length > 0) {
        setSuccessMessage(`已生成 ${generatedLabels.length} 个解法：${generatedLabels.join('、')}`);
      }
      if (errors.length > 0) {
        setAssessError(errors.join('\n'));
      }
      if (generatedLabels.length > 0 && errors.length === 0) {
        setModalOpen(false);
      }
    } finally {
      setGenerating(false);
    }
  };

  return (
    <>
      <TtqWorkbenchShell
        ttqaId={ttqaId}
        title="一题多解工作台"
        angleId="multi_solution"
        loading={ctx.loading}
        error={ctx.error}
        restoreParticleIds={[particleId]}
        toolbar={(
          <TtqWorkbenchToolbar
            showFocusToggle={split}
            leftLabel="源题"
            rightLabel="解法"
            emphasis={emphasis}
            onEmphasisChange={setEmphasis}
          >
            <TtqWorkbenchToolbarAction
              disabled={assessing || generating}
              loading={assessing || generating}
              onClick={handleOpenAssess}
            >
              生成新解法
            </TtqWorkbenchToolbarAction>
          </TtqWorkbenchToolbar>
        )}
      >
        {successMessage ? <p className="text-sm text-primary">{successMessage}</p> : null}
        {actionError ? <p className="text-sm text-danger">{actionError}</p> : null}
        <TtqWorkbenchFocusLayout
          split={split}
          emphasis={emphasis}
          source={(
            <TtqWorkbenchSourceParticle
              particleId={particleId}
              particle={ctx.particle}
              particleLoading={ctx.loading}
              sourceImage={ctx.sourceImage}
              imageLoading={ctx.imageLoading}
            />
          )}
          derived={<TtqWorkbenchDerivedList title="解法分支" items={toWorkspaceDerivedItems(derived)} emptyText="暂无衍生解法，点击「生成新解法」先评估再生成" returnTo={returnTo} />}
        />
      </TtqWorkbenchShell>

      <TtqMultiSolutionAssessModal
        open={modalOpen}
        assessing={assessing}
        generating={generating}
        assessment={assessment}
        assessError={assessError}
        selectedIndexes={selectedIndexes}
        imageModels={imageModels}
        imageModel={imageModel}
        onImageModelChange={setImageModel}
        onToggleIndex={handleToggleIndex}
        onClose={() => {
          if (!generating) setModalOpen(false);
        }}
        onRetryAssess={() => { void runAssess(); }}
        onConfirmGenerate={() => { void handleConfirmGenerate(); }}
      />
    </>
  );
}
